import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';

type UseAutoReadOTPOptions = {
  /** Called when a code is received (e.g., to set your form input) */
  onCode?: (code: string) => void;
  /** How long to wait before giving up (seconds). Chrome itself times out too. */
  timeoutSec?: number;
  /** Start listening automatically on mount */
  autoStart?: boolean;
};

type AutoReadOTPStatus = 'idle' | 'listening' | 'received' | 'timeout' | 'error';

type OTPCredential = {
  code: string;
  type: 'otp';
};

type AutoReadOTPNavigator = Navigator & {
  credentials: {
    get: (options: { otp: { transport: Array<'sms'> }; signal: AbortSignal }) => Promise<OTPCredential>;
  };
};

export function useAutoReadOtp({ onCode, timeoutSec = 60, autoStart = false }: UseAutoReadOTPOptions = {}) {
  const { t } = useTranslation('digital-forms-experience');

  const [status, setStatus] = useState<AutoReadOTPStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const onCodeRef = useRef(onCode);

  // Update ref when onCode changes
  useEffect(() => {
    onCodeRef.current = onCode;
  }, [onCode]);

  // Checking if WebOTP is supported
  const isSupported = typeof window !== 'undefined' && 'OTPCredential' in window;

  const abortRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setStatus('idle');
  }, []);

  const start = useCallback(async () => {
    if (!isSupported) return;

    // ensure previous run is canceled
    stop();

    // reset state
    setError(null);
    setStatus('listening');

    const ac = new AbortController();
    abortRef.current = ac;

    // hard timeout (Chrome also has its own internal timeout)
    timeoutRef.current = window.setTimeout(() => {
      if (!ac.signal.aborted) {
        ac.abort();
        setStatus('timeout');
      }
    }, timeoutSec * 1000);

    try {
      const cred = await (navigator as AutoReadOTPNavigator).credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal,
      });

      if (cred?.code) {
        setStatus('received');
        onCodeRef.current?.(cred.code);
      } else {
        setStatus('error');
        setError(t('No code received.'));
      }
    } catch (err: any) {
      setStatus((s) => (s === 'timeout' ? 'timeout' : 'error'));
      setError(err?.message || t('WebOTP failed.'));
    } finally {
      // clear our timeout holder
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      abortRef.current = null;
    }
  }, [isSupported, stop, t, timeoutSec]);

  useEffect(() => {
    if (autoStart && isSupported) {
      // Start listening as soon as the page/form is ready
      start();
    }
    // clean up on unmount
    return () => stop();
  }, [autoStart, isSupported, start, stop]);

  return {
    /** Begin listening for the SMS (call again if you retried/resend) */
    start,
    /** Abort listening (e.g., on manual submit or navigating away) */
    stop,
    /** True only on Android Chrome + HTTPS with WebOTP support */
    isSupported,
    /** Current state for UI: idle | listening | received | timeout | error */
    status,
    /** Any error message if status === "error" */
    error,
  };
}
